/**
 * CriticalErrorDialogComponent
 *
 * Purpose
 * - Displays a blocking, user-friendly error message with two actions: Continue or Log out.
 * - Emits a simple result to the opener instead of performing logout directly, avoiding DI cycles during error handling.
 *
 * Usage
 * - Opened by GlobalErrorHandler (or any caller) via MatDialog:
 *     const ref = dialog.open(CriticalErrorDialogComponent, {
 *       disableClose: true,
 *       data: { message: '...' }
 *     });
 *     ref.afterClosed().subscribe(result => {
 *       if (result === 'logout') auth.logout();
 *     });
 *
 * Changes made
 * - Removed direct dependency on AuthenticationService to break constructor-time DI cycles seen during bootstrap errors.
 * - Kept templateUrl/styleUrls, fields (htmlContent, confirmMessage, isLoggingOut), and public method names.
 * - Modified logout() to close the dialog with 'logout' so the opener (GlobalErrorHandler) triggers the actual logout.
 * - closeDialog() now closes with 'continue' (callers that don't care can ignore the result).
 *
 * Notes
 * - CSS and other UI logic remain unchanged. The only functional change is that the dialog no longer calls logout itself.
 *   This isolates authentication logic to the opener and prevents circular dependencies.
 */

import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-critical-error-dialog',
  templateUrl: './critical-error-dialog.component.html',
  styleUrls: ['./critical-error-dialog.component.css']
})
export class CriticalErrorDialogComponent {
  htmlContent: string;
  public confirmMessage: string;
  isLoggingOut: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CriticalErrorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.htmlContent = data.message;
  }

  // Preserve method name for template compatibility, but delegate actual logout to the opener.
  logout() {
    this.isLoggingOut = true;
    // Emit intent; GlobalErrorHandler (or opener) performs AuthenticationService.logout()
    this.dialogRef.close('logout');
  }

  closeDialog() {
    // Explicitly signal continue (opener may ignore the result)
    this.dialogRef.close('continue');
  }
}
